import { format } from 'date-fns'
import { useMemo } from 'react'
import { ComparisonLineChart, LINE_CHART_EMPTY_DATA } from 'src/components/analytics/charts/ComparisonLineChart'
import { NoData } from 'src/components/analytics/common/NoData'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import { ComparisonLineChartDataType } from 'src/components/analytics/types/comparison-line-chart'
import { useChartData } from 'src/components/overview/useChartData'
import { formatDateRange } from 'src/components/sso-analytics/charts/TotalIdentificationRate/parser'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { formatNumberToTwoDecimals } from 'src/lib/utils/analytics'
import { ChartSkeleton } from '../common/Skeleton'
import { getTimeIncrement, sortDataByDate } from '../common/utils'

export default function AutoLoggedInSessions() {
  const {
    getters: { selectedTimeScale, refreshKey, dateFilter }
  } = useAnalytics()

  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.conversion_auto_logged_in_sessions,
      dependencies: {
        endpoints: ['/identification-rate-over-time', '/identification-rate']
      }
    },
    refreshKey
  })


  const autoLoggedInData: ComparisonLineChartDataType & {
    totalAutoLoggedInPercentage: number
    autoLoggedInPercentageChange: number
  } = useMemo(() => {
    const rawData = data[0]?.data?.result?.widgetData?.data?.[0]?.data
    const rawComparedData = data[0]?.data?.result?.widgetData?.compared_data?.[0]?.data

    const currentPercentage = formatNumberToTwoDecimals(
      data[1]?.data?.result?.widgetData?.data?.[0]?.data?.[0]?.auto_identified_percentage || 0
    )
    const comparedPercentage = formatNumberToTwoDecimals(
      data[1]?.data?.result?.widgetData?.compared_data?.[0]?.data?.[0]?.auto_identified_percentage || 0
    )

    if (rawData && rawData.every((item: any) => item.total_identified === 0)) {
      return { ...LINE_CHART_EMPTY_DATA, totalAutoLoggedInPercentage: 0, autoLoggedInPercentageChange: 0 }
    }

    if (!rawData || rawData.length === 0) {
      return { ...LINE_CHART_EMPTY_DATA, totalAutoLoggedInPercentage: 0, autoLoggedInPercentageChange: 0 }
    }

    const currentData = rawData.sort(sortDataByDate)
    let previousData: any[] = []
    if (rawComparedData) {
      previousData = rawComparedData.sort(sortDataByDate)
    }

    const currentRange =
      new Date(currentData[currentData.length - 1].date).getTime() - new Date(currentData[0].date).getTime()
    let previousRange = 0
    if (previousData.length > 0) {
      previousRange =
        new Date(previousData[previousData.length - 1].date).getTime() - new Date(previousData[0].date).getTime()
    }

    const isCurrentPrimary = currentRange >= previousRange
    const primaryDataset = isCurrentPrimary ? currentData : previousData

    const dataIterator = (dataset: any[]) =>
      dataset.map((item, index) => ({
        date: new Date(item.date),
        displayDate: new Date(new Date(primaryDataset[0].date).getTime() + index * getTimeIncrement(selectedTimeScale)),
        rate: formatNumberToTwoDecimals(item.auto_identified_percentage || 0),
        actualDate: new Date(item.date)
      }))

    const mappedCurrentData = dataIterator(currentData)
    const mappedPreviousData = dataIterator(previousData)

    return {
      currentWeekData: mappedCurrentData,
      previousWeekData: mappedPreviousData,
      primaryDataset: isCurrentPrimary ? mappedCurrentData : mappedPreviousData,
      secondaryDataset: isCurrentPrimary ? mappedPreviousData : mappedCurrentData,
      isCurrentPrimary,
      currentDateRange: {
        from: new Date(currentData[0].date),
        to: new Date(currentData[currentData.length - 1].date)
      },
      previousDateRange:
        previousData.length > 0
          ? {
              from: new Date(previousData[0].date),
              to: new Date(previousData[previousData.length - 1].date)
            }
          : null,
      totalAutoLoggedInPercentage: currentPercentage,
      autoLoggedInPercentageChange:
        dateFilter.comparison !== 'none' ? formatNumberToTwoDecimals(currentPercentage - comparedPercentage) : 0
    }
  }, [data, selectedTimeScale])

  const currentDateRange = autoLoggedInData.currentDateRange
  const previousDateRange = autoLoggedInData.previousDateRange

  const currentDateRangeLegend =
    autoLoggedInData.currentWeekData.length === 1
      ? format(currentDateRange.from, 'MMM dd, yyyy')
      : formatDateRange(currentDateRange.from, currentDateRange.to, selectedTimeScale)

  const previousDateRangeLegend = previousDateRange
    ? formatDateRange(previousDateRange.from, previousDateRange.to, selectedTimeScale)
    : null

  const totalAutoLoggedInPercentage = autoLoggedInData.totalAutoLoggedInPercentage
  const autoLoggedInPercentageChange = autoLoggedInData.autoLoggedInPercentageChange

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (autoLoggedInData.currentWeekData.length > 0) {
      return (
        <>
          <div className='flex items-center gap-2 my-2'>
            <div className='text-[18px] font-bold text-zinc-900 mb-1'>{totalAutoLoggedInPercentage.toFixed(2)}%</div>
            <TrendIndicator value={autoLoggedInPercentageChange} formatter={value => `${Math.abs(value)}%`} />
          </div>
          <div className='w-full h-[84%]'>
            <ComparisonLineChart
              data={autoLoggedInData}
              isNoComparison={false}
              selectedTimeScale={selectedTimeScale}
              tooltipTitle='Auto logged-in sessions'
            />
          </div>
          <div className='rounded-lg flex-1'>
            <div className='flex gap-6 mb-4 px-4 text-zinc-400 text-xs'>
              <div className='flex items-center gap-2'>
                <div className='w-8 h-1 bg-primary-dark rounded-full'></div>
                <span>{currentDateRangeLegend}</span>
              </div>
              {previousDateRangeLegend && (
                <div className='flex items-center gap-2'>
                  <div className='w-8 h-1 border-t-2 border-dashed border-zinc-400'></div>
                  <span>{previousDateRangeLegend}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}
